import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewEncapsulation, input } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { MenuController, IonicModule } from '@ionic/angular';
import { Observable, Subject, map, of, switchMap, takeUntil } from 'rxjs';
import { catchError, filter, tap } from 'rxjs/operators';
import { PortalAuthService } from '../../auth/portal-auth.service';
import { PortalUser } from '../../domain/portal-user.model';
import { MenuConfigService } from '../../services/menu-config.service';
import { UserApiService, UserOrganization } from '../../services/user-api.service';
import { ActiveTenantService } from '../../services/active-tenant.service';
import { SignalrMessageAdapter } from '../../eventhub/signalr-message-adapter';
import { ShellOverlayService } from '../../services/shell-overlay.service';
import { TenantSwitcherService } from '../../services/tenant-switcher.service';
import { TooltipDirective } from '../../directives/tooltip.directive';
import { myProfileUrl } from '../../utils/my-links.util';
import { AsyncPipe } from '@angular/common';
import { VersionService } from '../../services/version.service';
import { TranslatePipe } from '@ngx-translate/core';
import { avatarUrl } from 'src/app/core/utils/avatar.util';

/**
 * Header avatar button + profile popover (tenant switching, profile, logout).
 * Receives the shared user$ from the shell; coordinates open/close with the
 * notification panel via ShellOverlayService. Host uses display:contents so it
 * sits transparently inside the toolbar's ion-buttons.
 */
@Component({
    selector: 'app-user-menu',
    templateUrl: './user-menu.component.html',
    styleUrls: ['./user-menu.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        IonicModule,
        TooltipDirective,
        RouterLink,
        AsyncPipe,
        TranslatePipe,
    ],
})
export class UserMenuComponent implements OnInit, OnDestroy {
  readonly user$ = input.required<Observable<PortalUser | null>>();

  readonly isPlatformAdmin$ = this.portalAuth.isPlatformAdmin$;
  /** Tenants with the ticket system get their support page; everyone else the public contact form. */
  readonly supportRoute$ = this.menuConfig.hasSupport$().pipe(map(has => has ? '/support' : '/contact'));
  readonly isOpen$ = this.overlay.open$.pipe(map(p => p === 'profile'));
  readonly spaVersion = this.versions.spaVersion;
  readonly avatarUrl = avatarUrl;
  readonly apiVersion$ = this.versions.apiVersion$;

  /** My-SPA settings page: language, mail language and appearance for all apps. */
  readonly myProfileUrl = myProfileUrl();

  organizations: UserOrganization[] = [];
  defaultOrgId: string | null = null;
  orgsError = false;
  private orgsLoaded = false;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly portalAuth: PortalAuthService,
    private readonly userApi: UserApiService,
    private readonly activeTenant: ActiveTenantService,
    private readonly signalr: SignalrMessageAdapter,
    private readonly tenantSwitcher: TenantSwitcherService,
    private readonly menuCtrl: MenuController,
    private readonly router: Router,
    private readonly overlay: ShellOverlayService,
    private readonly cdr: ChangeDetectorRef,
    private readonly menuConfig: MenuConfigService,
    private readonly versions: VersionService,
  ) {}

  ngOnInit(): void {
    // Track the user's preferred default tenant (Auth0 org id) for the star marker.
    this.user$().pipe(takeUntil(this.destroy$))
      .subscribe(u => { this.defaultOrgId = u?.defaultOrgId ?? null; this.cdr.markForCheck(); });

    // Lazy-load the user's organizations the first time the popover opens
    // (the trigger button lives in the toolbar and only toggles the overlay).
    this.overlay.open$.pipe(
      filter(p => p === 'profile' && !this.orgsLoaded),
      switchMap(() => this.userApi.getOrganizations().pipe(
        tap(() => { this.orgsLoaded = true; this.orgsError = false; }),
        catchError(err => {
          // Do NOT set orgsLoaded here: leaving it false means the filter above
          // still lets the next popover open retry the load instead of being
          // permanently stuck on an empty list, and returning of([]) (instead of
          // rethrowing) keeps this outer subscription alive for future opens.
          // orgsError, by contrast, IS sticky until a retry succeeds, so the
          // template can tell "genuinely 0/1 orgs" apart from "failed to load".
          console.warn('Failed to load organizations for user menu; will retry next time the popover opens.', err);
          this.orgsError = true;
          return of([] as UserOrganization[]);
        }),
      )),
      takeUntil(this.destroy$),
    ).subscribe(orgs => { this.organizations = orgs; this.cdr.markForCheck(); });
  }

  /** Mark an organization as the user's preferred default (optimistic UI update). */
  setDefault(orgId: string, event: Event): void {
    event.stopPropagation();
    this.userApi.setDefaultTenant(orgId).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => { this.defaultOrgId = orgId; this.cdr.markForCheck(); },
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  close(): void { this.overlay.close(); }

  async navigateToSettings(): Promise<void> {
    this.overlay.close();
    await this.menuCtrl.close();
    this.router.navigate(['/settings']);
  }

  switchTenant(orgId: string): void {
    this.overlay.close();
    this.portalAuth.loginWithOrg(orgId);
  }

  /** Admin only: pick a tenant to "act as". */
  async openTenantSwitcher(): Promise<void> {
    this.overlay.close();
    await this.tenantSwitcher.open();
  }

  logout(): void {
    this.activeTenant.clear();
    void this.signalr.disconnect();
    this.portalAuth.logout();
  }
}
